/**
 * LessonCard 관련 헬퍼 함수들
 * (기존 LessonCardViewModel의 단순 계산 로직 분리)
 */

import { translate, type Locale } from '@shared/config/i18n'
import { dayjs, DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import { ComponentProps } from 'react'
import { match, P } from 'ts-pattern'

import { BadgeV1 } from '@podo-app/design-system-temp'

import {
  ALLOW_CHANGE_TIME,
  CLASS_STATUS,
  CLASS_STATUS_KR_MAP,
  type ClassStatus,
  LESSON_STATE,
  type LessonState,
  MAX_PRESTUDY_TIME,
} from '../../model/constants/card'
import type {
  ChangeAllowedFields,
  LessonStateFields,
  LessonStatusFields,
  LessonTimeFields,
  MicrophonePermissionFields,
  TrialLessonStatusFields,
} from '../../model/types/card'
import { canEnterClassroom } from './action'

/**
 * 레슨 상태 계산 (최소 필드만 필요)
 */
export const calculateLessonStatus = (lesson: LessonStatusFields): ClassStatus => {
  const classStatus = CLASS_STATUS_KR_MAP[lesson.classStatus as keyof typeof CLASS_STATUS_KR_MAP]

  if (
    classStatus === CLASS_STATUS.COMPLETED ||
    classStatus === CLASS_STATUS.ABSENT ||
    classStatus === CLASS_STATUS.CANCELED ||
    classStatus === CLASS_STATUS.RESERVED
  ) {
    return classStatus
  }

  if (lesson.preStudyTime === null) {
    return CLASS_STATUS.NOT_RESERVED
  }

  if (lesson.preStudyTime < MAX_PRESTUDY_TIME) {
    return CLASS_STATUS.PRE_STUDY
  }

  if (lesson.preStudyTime >= MAX_PRESTUDY_TIME) {
    return CLASS_STATUS.PRE_STUDY_COMPLETED
  }

  throw new Error('INVALID_STATUS')
}

/**
 * 체험 레슨 상태 계산 (최소 필드만 필요)
 */
export const calculateTrialLessonStatus = (lesson: TrialLessonStatusFields): ClassStatus => {
  const { classStatus, classId, teacherId } = lesson
  const normalizedClassStatus = CLASS_STATUS_KR_MAP[classStatus as keyof typeof CLASS_STATUS_KR_MAP]

  const isFinishedStatus =
    normalizedClassStatus === CLASS_STATUS.COMPLETED ||
    normalizedClassStatus === CLASS_STATUS.ABSENT ||
    normalizedClassStatus === CLASS_STATUS.CANCELED

  if (!isFinishedStatus && classId !== null && teacherId === 0) {
    return CLASS_STATUS.NOT_RESERVED
  } else if (normalizedClassStatus === CLASS_STATUS.RESERVED && classId !== null && teacherId !== 0) {
    return CLASS_STATUS.RESERVED
  }

  return calculateLessonStatus(lesson)
}

/**
 * 레슨 상태에 따른 뱃지 색상
 */
export const getStatusColor = (status: ClassStatus): ComponentProps<typeof BadgeV1>['variant'] => {
  return match(status)
    .with(CLASS_STATUS.RESERVED, () => 'primary' as const)
    .with(CLASS_STATUS.COMPLETED, () => 'blue' as const)
    .with(CLASS_STATUS.CANCELED, () => 'error' as const)
    .with(CLASS_STATUS.NOT_RESERVED, () => 'expire' as const)
    .with(CLASS_STATUS.ABSENT, () => 'error' as const)
    .otherwise(() => 'primary' as const)
}

/**
 * 예약 가능 여부 (예습 8분 이상 완료)
 */
export const isBookingEnabled = (preStudyTime: number | null): boolean => {
  return (preStudyTime ?? 0) >= MAX_PRESTUDY_TIME
}

/**
 * 예습 진행률 계산 (0-100%)
 */
export const calculatePreStudyProgress = (preStudyTime: number | null): number => {
  return ((preStudyTime ?? 0) / MAX_PRESTUDY_TIME) * 100
}

/**
 * 예약 변경 불가 여부 - 최소 필드만 필요
 */
export const isChangeNotAllowedForLesson = (
  lesson: ChangeAllowedFields | number | null,
  currentTime: number = dayjs().unix(),
): boolean => {
  // 숫자 또는 null을 받으면 기존 방식으로 처리 (하위 호환)
  const startDateTime = typeof lesson === 'object' && lesson !== null ? lesson.unixClassStartDatetime : lesson

  if (startDateTime === null) {
    return false
  }
  return startDateTime - ALLOW_CHANGE_TIME < currentTime
}

/**
 * 마이크 권한 필요 여부 (레슨 시작 5분 전부터) - 최소 필드만 필요
 */
export const needsMicrophonePermission = (
  lesson: MicrophonePermissionFields | number | null,
  unixClassStartDatetime?: number | null,
  currentTime: number = Date.now(),
): boolean => {
  // 객체를 받으면 새로운 방식
  if (typeof lesson === 'object' && lesson !== null) {
    const { teacherId, unixClassStartDatetime } = lesson
    if (!teacherId || teacherId === 0 || !unixClassStartDatetime) {
      return false
    }
    return canEnterClassroom(unixClassStartDatetime, Math.floor(currentTime / 1000))
  }

  // 개별 파라미터로 받으면 기존 방식 (하위 호환)
  const teacherId = lesson
  if (!teacherId || teacherId === 0 || !unixClassStartDatetime) {
    return false
  }
  return canEnterClassroom(unixClassStartDatetime, Math.floor(currentTime / 1000))
}

/**
 * 레슨 시간 포맷팅 - 최소 필드만 필요
 */
export const formatLessonTime = (
  lesson: LessonTimeFields | number | null,
  unixClassEndDatetime?: number | null,
  teacherName?: string | null,
  timezone: string = DEFAULT_TIMEZONE,
  locale: Locale = 'ko',
): string | null => {
  const separatorByLocale: Record<Locale, string> = {
    ko: 'ㅣ',
    en: '|',
    ja: '｜',
  }

  // 객체를 받으면 새로운 방식
  if (typeof lesson === 'object' && lesson !== null) {
    const { unixClassStartDatetime, unixClassEndDatetime, teacherName } = lesson
    if (!unixClassStartDatetime || !unixClassEndDatetime) {
      return null
    }

    const startDate = dayjs.unix(unixClassStartDatetime).tz(timezone).locale(locale)
    const endDate = dayjs.unix(unixClassEndDatetime).tz(timezone).locale(locale)

    return `${startDate.format(translate(locale, 'common.date.monthDayTime'))} ~ ${endDate.format('HH:mm')} ${
      separatorByLocale[locale]
    } ${teacherName}`
  }

  // 개별 파라미터로 받으면 기존 방식 (하위 호환)
  const unixClassStartDatetime = lesson
  if (!unixClassStartDatetime || !unixClassEndDatetime) {
    return null
  }

  const startDate = dayjs.unix(unixClassStartDatetime).tz(timezone).locale(locale)
  const endDate = dayjs.unix(unixClassEndDatetime).tz(timezone).locale(locale)

  return `${startDate.format(translate(locale, 'common.date.monthDayTime'))} ~ ${endDate.format('HH:mm')} ${
    separatorByLocale[locale]
  } ${teacherName}`
}

/**
 * 레슨 히스토리 유무
 */
export const hasLessonHistory = (classCount: number, compClassCount: number): boolean => {
  return classCount > 0 && compClassCount > 0
}

/**
 * 레슨 상태 (완료/예습필요) 계산 - 최소 필드만 필요
 */
export const calculateLessonState = (
  lesson: LessonStateFields | number | null,
  compClassCount?: number,
): LessonState => {
  // 객체를 받으면 새로운 방식
  if (typeof lesson === 'object' && lesson !== null) {
    return match({
      preStudyTime: lesson.preStudyTime,
      compClassCount: lesson.compClassCount,
    })
      .with(
        {
          preStudyTime: null,
          compClassCount: P.when((count) => count > 0),
        },
        () => LESSON_STATE.COMPLETED,
      )
      .otherwise(() => LESSON_STATE.PRESTUDY_REQUIRED)
  }

  // 개별 파라미터로 받으면 기존 방식 (하위 호환)
  const preStudyTime = lesson
  return match({
    preStudyTime,
    compClassCount: compClassCount ?? 0,
  })
    .with(
      {
        preStudyTime: null,
        compClassCount: P.when((count) => count > 0),
      },
      () => LESSON_STATE.COMPLETED,
    )
    .otherwise(() => LESSON_STATE.PRESTUDY_REQUIRED)
}
